import { gql } from '@apollo/client'
import { orderFieldsQuery } from '../../queries/order/query'

/**
 * Get's the string for `confirmOrder` mutation.
 * This mutation confirms one created order and returns it.
 * @internal
 */

/*
 * `confirmOrder` lives on the parallel `orderStatusChange` schema, not on
 * `order` — which is why a check against the order types file alone concludes
 * only the plural `confirmOrders` exists. In the storefront repo a mutation's
 * exposure is decided by the `mergeSchemas` list in
 * `packages/user/src/schema/index.ts`, and this one has been on it since
 * COM-3844.
 *
 * Both ids are sent, and neither narrows the lookup, because the resolver's
 * filter is an `$or`:
 *
 *   { $or: [{ _id: id }, { sessionUid }], status: { $in: [CREATED, SUGGESTED] } }
 *
 * and it then requires **exactly one** match. Since the clauses are unioned,
 * the match set is `{the order with this _id} ∪ {every confirmable order in
 * the session}`, so adding `id` cannot exclude the session's other confirmable
 * orders. On a session that legitimately holds two — an order in `SUGGESTED`
 * does not block a new `CREATED` one — the call fails with "Only 1 order
 * allowed per ID or SessionUID" whatever this document sends. That rejection
 * is classified as ambiguous rather than terminal; `isAmbiguousOrderMatchError`
 * carries the reasoning.
 *
 * They are both sent anyway, for two unrelated reasons. `sessionUid` because
 * `validateConfirmOrder` requires it and the session middleware needs it to
 * scope the call to the caller. `id` because the alternative is worse: with
 * `sessionUid` absent the resolver evaluates
 * `new Types.ObjectId(sessionUid ?? undefined)`, which mints a *fresh random*
 * ObjectId, so that arm matches nothing — but the joi schema rejects an
 * omitted `sessionUid` before any of that runs.
 *
 * What sending `sessionUid` does **not** do is put the read on a
 * customer-scoped path. There is no such path here: the filter carries no
 * `customerUid` term, and the resolver validates
 * `getActiveSession(order.sessionUid)` — the session recorded on whichever
 * order it found, not the caller's. The `order` query's scoping reasoning does
 * not transfer to this mutation.
 *
 * `data: OrderInput` is deliberately not declared. The resolver reads exactly
 * one field from it, `data.originalId`, and only as an override for an order
 * that already carries one — `dataOriginalId != null` is the guard, so an
 * absent `data` leaves `order.originalId` as it stands, which is what an order
 * created moments ago by this same flow wants. joi's `orderStatusChangeSchema`
 * does not require it. There is nothing cinnamon knows here that the order
 * does not already say.
 *
 * The mutation must **not** be called for an order in `WAITING_CALLBACK`: the
 * status filter above will not match it, so the call cannot succeed and can
 * only produce a spurious failure over an order that is progressing normally.
 * The caller branches on the created order's status for that reason. Note the
 * shape that failure takes — zero matches is `NOT_FOUND` (`404`), not the
 * `400` a decline produces, which is why "the order was not in a confirmable
 * state" belongs in the caller's ambiguous bucket.
 */
const confirmOrderMutation = gql`
  mutation ConfirmOrder(
    $storefrontName: String!
    $sessionUid: ID
    $id: ID
  ) {
    confirmOrder(
      storefrontName: $storefrontName
      sessionUid: $sessionUid
      id: $id
    ) {
      ${orderFieldsQuery}
    }
  }
`

export default confirmOrderMutation
